import React, { useEffect, useRef, useState } from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import {
  Text,
  Box,
  VStack,
  Button,
  Switch,
  useDisclosure,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useToast,
} from '@chakra-ui/react';
import { patchData } from '../../../../utilities/axios';
import { updateUserDetails } from '../../../../redux/actions/user';
import { useDispatch, useSelector } from 'react-redux';
import { CheckIcon } from '@chakra-ui/icons';
import * as N from '../../../../utilities/notifications';

const ProfileCompletion = ({ profile, onView }) => {
  const [loadCompletion, setLoadCompletion] = useState(false);
  const [profileCompletion, setProfileCompletion] = useState(null);
  const [avatarComplete, setAvatarComplete] = useState(0);
  const [descriptionComplete, setDescriptionComplete] = useState(0);
  const [summaryComplete, setTaglineComplete] = useState(0);
  const [practiceAreasComplete, setPracticeAreasComplete] = useState(0);
  const [therapyTypesComplete, setTherapyTypesComplete] = useState(0);
  const [availabilityComplete, setAvailabilityComplete] = useState(0);
  const [clientGroupsComplete, setClientGroupsComplete] = useState(0);
  const [sessionsComplete, setSessionsComplete] = useState(0);
  const [nameComplete, setNameComplete] = useState(0);
  const [feesComplete, setFeesComplete] = useState(0);
  const [qualificationsComplete, setQualificationsComplete] = useState(0);
  const [genderComplete, setGenderComplete] = useState(0);
  const [contactAddressComplete, setContactAddressComplete] = useState(0);

  const user = useSelector((state) => state.user.userDetails);
  const token = useSelector((state) => state.user.JWTToken);
  const dispatch = useDispatch();
  const toast = useToast();

  // out of office switch
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef();

  // check profile
  useEffect(() => {
    if(!user || !profile) {
      return;
    }

    setGenderComplete(user.gender?.length > 0 || profile.gender?.length > 0 ? 1 : 0);
    setSessionsComplete(profile.sessionTypes?.length > 0 ? 1 : 0);
    setAvatarComplete(profile.profileImage !== null ? 1 : 0);
    setDescriptionComplete(profile.aboutMe?.length > 0 ? 1 : 0);
    setTaglineComplete(profile.summary?.length > 0 ? 1 : 0);
    setPracticeAreasComplete(profile.practiceAreas?.length > 0 ? 1 : 0);
    setTherapyTypesComplete(profile.practitionerModalities?.length > 0 ? 1 : 0);
    setAvailabilityComplete(
      profile.availability.filter((arr) => arr.some((elem) => elem === true))[0]
        ? 1
        : 0
    );
    setClientGroupsComplete(profile.clientGroups.length > 0 ? 1 : 0);
    setNameComplete(profile.fullname?.length > 0 ? 1 : 0);
    setFeesComplete(Number(profile.averagePrice) > 0 ? 1 : 0);
    setQualificationsComplete(profile?.qualifications[0]?.length > 0 ? 1 : 0);
    setContactAddressComplete(profile?.contactAddress?.address ? 1 : 0);
    setLoadCompletion(true);
  }, [
    user,
    profile,
  ]);

  useEffect(() => {
    if (loadCompletion) {
      setProfileCompletion(
        Math.ceil(
          (Number(
            sessionsComplete +
            avatarComplete +
            descriptionComplete +
            summaryComplete +
            practiceAreasComplete +
            therapyTypesComplete +
            availabilityComplete +
            clientGroupsComplete +
            feesComplete +
            nameComplete +
            qualificationsComplete +
            genderComplete +
            contactAddressComplete
          ) /
            13) *
          100
        )
      );
    }
  }, [
    sessionsComplete,
    avatarComplete,
    descriptionComplete,
    summaryComplete,
    practiceAreasComplete,
    therapyTypesComplete,
    availabilityComplete,
    clientGroupsComplete,
    nameComplete,
    feesComplete,
    qualificationsComplete,
    loadCompletion,
    genderComplete,
    contactAddressComplete,
  ]);

  const updateVisibility = async (showProfile) => {
    if (!showProfile) onClose();
    // Construct new profile
    let userUpdateProfile = {
      profileId: profile.profileId,
      practitionerType: 'therapist',
      outOfOffice: !showProfile,
    };

    try {
      await patchData(
        token,
        userUpdateProfile,
        `${process.env.NEXT_PUBLIC_NEW_USER_URL}profile`
      );

      // PractitionerType 1 = Therapist
      // PractitionerType 2 = Coach
      switch (profile.practitionerType) {
        case 'therapist':
          await dispatch(
            updateUserDetails({
              therapistProfile: {
                ...profile,
                ...userUpdateProfile
              },
            })
          );
          break;
        default:
          console.error(
            'PROBLEM SAVING out of office SECTION -practitionerType not found'
          );
          throw new Error('Unknown profile type.');
      }

      toast(N.updateDetailsSuccess);
    } catch (error) {
      console.error('PROBLEM SAVING out of office SECTION', error);
      toast(N.updateDetailsFail(error));
    }
  };

  const switchOOO = (switchState) => {
    if (!switchState) {
      // making invisible
      onOpen();
    } else {
      // making visible
      updateVisibility(true);
    }
  };

  const updateProfileCompletion = async (complete) => {
    // Construct new profile
    let userUpdateProfile = {
      profileId: profile.profileId,
      practitionerType: 'therapist',
      profileComplete: complete,
    };

    try {
      await patchData(
        token,
        userUpdateProfile,
        `${process.env.NEXT_PUBLIC_NEW_USER_URL}profile`
      );

      switch (profile.practitionerType) {
        case 'therapist':
          await dispatch(
            updateUserDetails({
              therapistProfile: {
                ...profile,
                ...userUpdateProfile
              },
            })
          );
          break;
        default:
          console.error(
            'PROBLEM SAVING PROFILE COMPLETE SECTION -practitionerType not found'
          );
          throw new Error('Unknown profile type.');
      }
    } catch (error) {
      console.error('PROBLEM SAVING PROFILE COMPLETE', error);
    }
  };

  // Chart js config
  ChartJS.register(ArcElement, Tooltip, Legend);

  // helpers
  const renderProfileCompletionHeader = () => {
    if (loadCompletion && profileCompletion !== null) {

      let currentProfileCompletion = profileCompletion === 100;

      if (currentProfileCompletion !== profile.profileComplete) {
        updateProfileCompletion(currentProfileCompletion);
      }
    }

    switch (true) {
      case profileCompletion === 0:
        return (
          <Text>
            Lets get started! Please complete the steps below to ensure that
            your profile displays correctly in search results.
          </Text>
        );
      case 0 < profileCompletion && profileCompletion < 51:
        return (
          <Text textStyle="body2">
            <Text color="tomato" fontWeight="bold">
              Your profile is currently {profileCompletion}% complete.
            </Text>
            Please complete the steps below to ensure that your profile displays
            correctly in search results.
          </Text>
        );
      case 51 < profileCompletion && profileCompletion < 100:
        return (
          <Text textStyle="body2">
            <Text color="tomato" fontWeight="bold">
              Your profile is currently {profileCompletion}% complete.
            </Text>{' '}
            Almost there, only a few steps left and your profile will be
            displayed in client search results.
          </Text>
        );
      case profileCompletion === 100:
        return (
          <Text textStyle="body1">
            <Text color="brand.400" fontWeight="bold">
              Congratulations! You have completed all our required settings
            </Text>
            <Text className='text-sm font-light'>
              Take a look at the advanced filters above in case you would like to add more detail
            </Text>
          </Text>
        );
    }
  };

  return (
    <Box
      backgroundColor={profile?.outOfOffice ? 'tomato' : 'white'}
      position="relative"
      width="100%"
    >
      {/* Alert for Out Of Office */}
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Set Out Of Office
            </AlertDialogHeader>

            <AlertDialogBody>
              Closing the office for a few days? <br></br>
              <br></br>Turning off visibility will temporarily remove your
              profile from appearing in any search results. <br></br>
              <br></br>When you are ready to be promoted to new clients again,
              just click the switch to switch visibility on.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose} variant="ghost">
                Cancel
              </Button>
              <Button
                variant="delete"
                onClick={() => updateVisibility(false)}
                ml={3}
                leftIcon={<CheckIcon />}
              >
                Set out of office
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
      {/* Title info */}
      <VStack alignItems="flex-start" position="relative" p="30px">
        <Box
          width="100%"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          {profile?.outOfOffice ? (
            <Text textStyle="h3" color="white" fontWeight="bold">
              You are currently out of office. Your profile will not appear in
              search results.
            </Text>
          ) : (
            <Text textStyle="h3">Profile completion</Text>
          )}

          <Box
            display="flex"
            as="Button"
            justifyContent="flex-end"
            color="brand.300"
            alignItems="center"
          >
            {' '}
            {profile?.outOfOffice ? (
              <Text
                textStyle="h6"
                color="white"
                fontWeight="bold"
                paddingRight="8px"
              >
                Make profile visible
              </Text>
            ) : (
                <Text
                  textStyle="h6"
                  color="brand.400"
                  fontWeight="bold"
                  paddingRight="8px"
                >
                  Profile visible
                </Text>
              )}
            <Switch
              size="lg"
              colorScheme="primary"
              isChecked={!profile?.outOfOffice}
              onChange={(e) => switchOOO(e.target.checked)}
            />
          </Box>
        </Box>

        {/* availability */}
        {loadCompletion && (
          <Box
            w="100%"
          //pt="26px"
          >
            {!profile?.outOfOffice && (
              <Box w="100%" display={{ base: 'block', md: 'flex' }}>
                <div className='flex justify-between items-center w-full'>
                  <div>
                    {renderProfileCompletionHeader()}
                  </div>
                  {profileCompletion === 100 && (
                    <button onClick={onView} className='rounded-md p-2 text-white bg-[#587500]'>
                      View Profile
                    </button>
                  )}
                </div>
              </Box>
            )}
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export default ProfileCompletion;
